import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Image,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { IconButton } from "react-native-paper";
import { useTheme } from "@/src/styles/theme-provider";

import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";
import LinkifiedText from "@/src/components/ui-kit/linkified-text";
import FormattedText from "@/src/components/ui-kit/formatted-text";
import { isNewsPost } from "@/src/utils/news-post.utils";
import { useLinkPreview } from "@/src/hooks/features/posts/use-link-preview";
import LinkPreviewCard from "./post/link-preview/link-preview-card";
import { LinkPreviewSkeleton } from "./post/link-preview/link-preview-skeleton";
import { usePostDetail } from "@/src/hooks/features/posts/use-post-detail";
import { CloseCircle, Send } from "iconsax-react-nativejs";
import ShareModal from "../action-modals/share-modal";
import PostDetailSkeleton from "./loading-skeletons/post-detail-skeleton";
import ScButton from "../ui-kit/button";
import { FileAttachment } from "./post/file-attachment";
import { MediaCarousel } from "./post/media-carousel";
import StickerLayer from "@/src/features/sticker-display/sticker-layer";
import { useStickersOn } from "@/src/features/sticker-display/use-stickers-on";
import { isImageUrl } from "./stickers/sticker-geometry";
import PostActions from "./post/post-actions";
import EmojiPicker from "rn-emoji-keyboard";
import { EmojiHappy } from "iconsax-react-nativejs";
import CommentsList from "./post/post-comments";
import PostHeader from "./post/post-header";
import NewsPostBanner from "./post/news-post-banner";
import ReactorModal from "./post-reactor-modal";
import ItemSelectorModal from "@/src/components/action-modals/item-selection/item-selection-modal";
import MentionedUserPill from "./mentioned-user-pill";
import { useGetUsers } from "@/src/services/api/groups/groups.hooks";
import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";

dayjs.extend(relativeTime);

type Props = Readonly<{
  visible: boolean;
  postId: string;
  initialIndex?: number;
  onClose: () => void;
}>;

const PostDetailModal = ({
  visible,
  postId,
  initialIndex = 0,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();

  // Reaction-count tap. The feed card has always offered this, but the detail
  // view — the one a "X reacted to your post" notification lands on — did not,
  // so following the notification was a dead end for the very question it
  // raises: who reacted?
  const [reactorModalVisible, setReactorModalVisible] = useState(false);
  const stickersOn = useStickersOn();
  const [bodyBox, setBodyBox] = useState({ w: 0, h: 0 });
  const [emojiOpen, setEmojiOpen] = useState(false);

  const {
    user,
    activePost,
    isPostLoading,
    isPostError,
    isPostMissing,
    mediaUrls,
    fileUrls,
    userReaction,
    handleReact,
    reactionCount,
    comments,
    isCommentsLoading,
    isCommentsError,
    refetchComments,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    commentText,
    handleCommentTextChange,
    isCommentDisabled,
    isCommentPending,
    handlePostComment,
    inputRef,
    replyTarget,
    handleReply,
    handleCancelReply,
    commentMentionedUsers,
    setCommentMentionedUsers,
    showCommentMentionModal,
    handleCommentMentionConfirm,
    handleCommentMentionCancel,
    shareModalVisible,
    setShareModalVisible,
    handleShareNotification,
    isSaved,
    handleToggleSave,
  } = usePostDetail({ postId, visible });
  const postImageIndexes = (activePost?.attachmentUrls ?? []).filter((u) => isImageUrl(u));

  const { data: linkPreview, isFetching: isLinkPreviewLoading } = useLinkPreview(
    activePost?.content ?? "",
  );

  const [userSearch, setUserSearch] = useState("");
  const {
    data: usersData,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: usersHasNextPage,
    isFetchingNextPage: usersIsFetchingNextPage,
  } = useGetUsers(userSearch);
  const users: SelectableItem[] = (
    usersData?.pages.flatMap((page) => page?.users ?? []) ?? []
  ).map((u) => ({
    itemId: u.itemId,
    displayName: u.displayName,
    userName: u.userName,
    profileImageUrl: u.profileImageUrl,
  }));

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View
        style={varStyle}
        className="flex-1 bg-black/40 justify-center items-center"
      >
        <KeyboardAvoidingView
          style={{
            flex: 1,
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
          behavior={Platform.OS === "ios" ? "padding" : undefined}
        >
          <View
            style={{ width: "90%", maxWidth: 700, maxHeight: "90%" }}
            className="bg-chalk rounded-2xl overflow-hidden"
          >
            {isPostLoading ? (
              <>
                <TouchableOpacity
                  onPress={onClose}
                  hitSlop={8}
                  className="absolute top-3 right-3 hover:bg-ash-3 hover:rounded-full"
                >
                  <CloseCircle size={24} color={themeVars["--color-slate-default"] as string} />
                </TouchableOpacity>
                <PostDetailSkeleton />
              </>
            ) : !activePost ? (
              /* Settled with nothing to show. A missing post is a *successful*
                 query returning null, so this used to fall into the skeleton
                 branch above and spin forever — the state a notification lands
                 in once its post has been deleted. Say so instead. */
              <View className="items-center gap-3 px-8 py-12">
                <TouchableOpacity
                  onPress={onClose}
                  hitSlop={8}
                  className="absolute top-3 right-3 hover:bg-ash-3 hover:rounded-full"
                >
                  <CloseCircle size={24} color={themeVars["--color-slate-default"] as string} />
                </TouchableOpacity>
                <Image
                  source={require("@/src/assets/images/empty.png")}
                  style={{ width: 180, height: 180 }}
                  resizeMode="contain"
                />
                <Text className="text-base font-semibold text-onyx text-center">
                  {t(isPostError ? "ERROR_LOADING_POST" : "LABEL_POST_UNAVAILABLE")}
                </Text>
                {isPostMissing && (
                  <Text className="text-sm text-slate text-center">
                    {t("HINT_POST_DELETED")}
                  </Text>
                )}
                <ScButton text="BTN_CLOSE" mode="outlined" onPress={onClose} />
              </View>
            ) : (
              <>
                <PostHeader post={activePost} showClose onClose={onClose} />
                <NewsPostBanner post={activePost} />
                <ScrollView
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={{ paddingBottom: 80 }}
                >
                  <View onLayout={(e) => setBodyBox({ w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height })}>
                  <View className="px-4 pb-1 mt-2">
                    {isNewsPost(activePost) ? (
                      <FormattedText className="text-sm leading-5 text-onyx">
                        {activePost.content}
                      </FormattedText>
                    ) : (
                      <LinkifiedText className="text-sm leading-5 text-onyx">
                        {activePost.content}
                      </LinkifiedText>
                    )}
                  </View>

                  {/* The same card the feed shows, so a post reads the same
                      way opened as it does scrolled past. */}
                  {linkPreview ? <LinkPreviewCard preview={linkPreview} /> : null}
                  {!linkPreview && isLinkPreviewLoading ? <LinkPreviewSkeleton /> : null}
                  {stickersOn && !postImageIndexes.length && bodyBox.w > 0 ? (
                    // Posts without images carry their stickers on the body (text / link preview).
                    <StickerLayer
                      stickers={activePost.stickers}
                      mediaIndex={-1}
                      viewerId={user?.itemId}
                      box={{ left: 0, top: 0, w: bodyBox.w, h: Math.max(bodyBox.h, 1) }}
                    />
                  ) : null}
                  </View>

                  {(fileUrls ?? []).length > 0 && (
                    <View className="px-4 my-2">
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{ gap: 8 }}
                      >
                        {(fileUrls ?? []).map((url) => (
                          <FileAttachment key={url} fileUrl={url} />
                        ))}
                      </ScrollView>
                    </View>
                  )}

                  {(mediaUrls ?? []).length > 0 && (
                    <MediaCarousel
                      media={mediaUrls ?? []}
                      initialIndex={initialIndex}
                      renderImageOverlay={
                        stickersOn
                          ? (uri, area) => (
                              <StickerLayer
                                stickers={activePost.stickers}
                                mediaIndex={(activePost.attachmentUrls ?? []).indexOf(uri)}
                                viewerId={user?.itemId}
                                fit={{ imageUri: uri, area }}
                              />
                            )
                          : undefined
                      }
                    />
                  )}

                  <PostActions
                    userReaction={userReaction?.type}
                    reactionCounts={reactionCount}
                    reactionTotal={Object.values(
                      reactionCount || {},
                    ).reduce((sum, val) => (sum ?? 0) + (val ?? 0), 0)}
                    onReact={handleReact}
                    commentCount={activePost.commentCount}
                    onShare={() => setShareModalVisible(true)}
                    onReactorCount={() => setReactorModalVisible(true)}
                  />

                  <CommentsList
                    comments={comments}
                    isLoading={isCommentsLoading}
                    isError={isCommentsError}
                    onRefresh={refetchComments}
                    onReply={handleReply}
                    onLoadMore={fetchNextPage}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                  />
                </ScrollView>

                <View className="absolute bottom-0 left-0 right-0 bg-chalk border-t border-ash-3">
                  {commentMentionedUsers.length > 0 && (
                    <View className="flex-row flex-wrap gap-1 px-4 pt-2">
                      {commentMentionedUsers.map((u) => (
                        <MentionedUserPill
                          key={u.itemId}
                          displayName={u.displayName ?? "Unknown"}
                          userId={u.userId}
                          isEditing
                          onRemove={() =>
                            setCommentMentionedUsers((prev) =>
                              prev.filter((m) => m.itemId !== u.itemId),
                            )
                          }
                        />
                      ))}
                    </View>
                  )}
                  <View className="pl-4 py-2 flex-row items-center">
                    <BlocksAvatar
                      userId={user?.itemId}
                      fallbackImageUrl={user?.profileImageUrl}
                      firstName={user?.firstName}
                      lastName={user?.lastName}
                      size={40}
                      className="mr-3"
                    />
                    {replyTarget && (
                      <TouchableOpacity
                        onPress={handleCancelReply}
                        hitSlop={8}
                        className="hover:bg-ash-3 hover:rounded-full"
                      >
                        <CloseCircle size={16} className="mr-2" />
                      </TouchableOpacity>
                    )}
                    <TextInput
                      ref={inputRef}
                      value={commentText}
                      onChangeText={handleCommentTextChange}
                      placeholder={
                        replyTarget
                          ? `${t("LABEL_REPLYING_TO")} ${replyTarget.author}...`
                          : t("LABEL_LEAVE_A_COMMENT")
                      }
                      style={{ color: themeVars["--color-onyx-default"] as string }}
                      placeholderTextColor={themeVars["--color-ash-1"] as string}
                      className="flex-1 border-ash-3 rounded-full px-4 py-2 text-sm bg-ash focus:outline focus:outline-primary"
                      onKeyPress={({ nativeEvent }) => {
                        if (
                          nativeEvent.key === "Enter" &&
                          !(nativeEvent as any).shiftKey &&
                          !isCommentDisabled &&
                          !isCommentPending
                        ) {
                          handlePostComment();
                        }
                      }}
                    />
                    <TouchableOpacity
                      testID="comment-emoji-button"
                      accessibilityLabel={t("LABEL_ADD_EMOJI")}
                      onPress={() => setEmojiOpen(true)}
                      hitSlop={8}
                      className="ml-2 p-1 rounded-full hover:bg-ash-3"
                    >
                      <EmojiHappy size={22} color={themeVars["--color-slate-default"] as string} />
                    </TouchableOpacity>
                    <EmojiPicker
                      open={emojiOpen}
                      onClose={() => setEmojiOpen(false)}
                      onEmojiSelected={(e: { emoji: string }) => {
                        handleCommentTextChange(`${commentText}${e.emoji}`);
                        inputRef.current?.focus?.();
                      }}
                      enableSearchBar
                      enableRecentlyUsed
                      categoryPosition="top"
                    />
                    <IconButton
                      loading={isCommentPending}
                      icon={() => (
                        <Send
                          color={
                            isCommentDisabled ? themeVars["--color-ash-2"] as string : themeVars["--color-primary-default"] as string
                          }
                          size={26}
                          variant={isCommentDisabled ? "Outline" : "Bold"}
                        />
                      )}
                      disabled={isCommentDisabled || isCommentPending}
                      iconColor={themeVars["--color-primary-default"] as string}
                      onPress={handlePostComment}
                    />
                  </View>
                </View>
              </>
            )}
          </View>
        </KeyboardAvoidingView>
      </View>

      <ShareModal
        visible={shareModalVisible}
        onClose={() => setShareModalVisible(false)}
        resourceId={activePost?.ItemId ?? ""}
        resource="post"
        onShare={handleShareNotification}
      />

      <ReactorModal
        showModal={reactorModalVisible}
        onClose={() => setReactorModalVisible(false)}
        reactions={reactionCount ?? {}}
        postId={activePost?.ItemId}
      />

      {/* `useGetUsers` pages 10 at a time, so without this the mention picker
          could only ever offer the first 10 users in the org. */}
      <ItemSelectorModal
        items={users}
        visible={showCommentMentionModal}
        titleKey="LABEL_MENTION_USERS"
        confirmButtonKey="LABEL_ADD"
        onClose={handleCommentMentionCancel}
        onConfirm={(_ids, items) => { handleCommentMentionConfirm(items); }}
        onEndReached={() => {
          if (usersHasNextPage && !usersIsFetchingNextPage) fetchNextUsersPage();
        }}
        isFetchingMore={usersIsFetchingNextPage}
        onSearchChange={setUserSearch}
        totalCount={usersData?.pages[0]?.count}
      />
    </Modal>
  );
};

export default PostDetailModal;
